import {useEffect, memo} from 'react';
import {useMap} from '@vis.gl/react-google-maps';

type LatLngLiteral = google.maps.LatLngLiteral;
type LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral;
type Padding = number | google.maps.Padding;
type ResolvedPadding = { top: number; right: number; bottom: number; left: number };

const LOCK_IN_PLACE_PADDING_FACTOR = 0.2;
const MIN_LOCK_RADIUS_METERS = 250;
const FIT_BOUNDS_PADDING_FACTOR = 0.25;
const SINGLE_POINT_FIT_RADIUS_METERS = 300;

function metersToLatDegrees(meters: number): number {
    return meters / 111320;
}

function metersToLngDegrees(meters: number, atLat: number): number {
    const latitudeCos = Math.max(Math.cos((atLat * Math.PI) / 180), 0.00001);
    return meters / (111320 * latitudeCos);
}

function expandBounds(bounds: LatLngBoundsLiteral, factor: number): LatLngBoundsLiteral {
    const centerLat = (bounds.north + bounds.south) / 2;
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const minLatOffset = metersToLatDegrees(MIN_LOCK_RADIUS_METERS);
    const minLngOffset = metersToLngDegrees(MIN_LOCK_RADIUS_METERS, centerLat);

    const latOffset = Math.max(latSpan * factor, minLatOffset);
    const lngOffset = Math.max(lngSpan * factor, minLngOffset);

    return {
        south: bounds.south - latOffset,
        north: bounds.north + latOffset,
        west: bounds.west - lngOffset,
        east: bounds.east + lngOffset,
    };
}

function expandBoundsAsymmetric(
    bounds: LatLngBoundsLiteral,
    factor: number,
    westMultiplier: number,
    eastMultiplier: number,
): LatLngBoundsLiteral {
    const centerLat = (bounds.north + bounds.south) / 2;
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const minLatOffset = metersToLatDegrees(MIN_LOCK_RADIUS_METERS);
    const minLngOffset = metersToLngDegrees(MIN_LOCK_RADIUS_METERS, centerLat);

    const latOffset = Math.max(latSpan * factor, minLatOffset);
    const lngOffset = Math.max(lngSpan * factor, minLngOffset);

    return {
        south: bounds.south - latOffset,
        north: bounds.north + latOffset,
        west: bounds.west - (lngOffset * westMultiplier),
        east: bounds.east + (lngOffset * eastMultiplier),
    };
}

function toLiteralBounds(bounds: google.maps.LatLngBounds): LatLngBoundsLiteral {
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    return {
        south: southWest.lat(),
        north: northEast.lat(),
        west: southWest.lng(),
        east: northEast.lng(),
    };
}

function resolveHorizontalBias(overlayPadding?: Padding): { westMultiplier: number; eastMultiplier: number } {
    const resolvedOverlayPadding = toPaddingObject(overlayPadding);
    const horizontalPaddingTotal = resolvedOverlayPadding.left + resolvedOverlayPadding.right;
    const overlayHorizontalBias = horizontalPaddingTotal > 0
        ? (resolvedOverlayPadding.left - resolvedOverlayPadding.right) / horizontalPaddingTotal
        : 0;

    if (overlayHorizontalBias > 0) {
        return { westMultiplier: 1.35, eastMultiplier: 0.65 };
    }

    if (overlayHorizontalBias < 0) {
        return { westMultiplier: 0.65, eastMultiplier: 1.35 };
    }

    return { westMultiplier: 1, eastMultiplier: 1 };
}

function buildLockBoundsFromPoints(points: LatLngLiteral[], overlayPadding?: Padding): LatLngBoundsLiteral {
    const { westMultiplier, eastMultiplier } = resolveHorizontalBias(overlayPadding);

    if (points.length === 1) {
        const point = points[0];
        const latDelta = metersToLatDegrees(MIN_LOCK_RADIUS_METERS);
        const lngDelta = metersToLngDegrees(MIN_LOCK_RADIUS_METERS, point.lat);

        return {
            south: point.lat - latDelta,
            north: point.lat + latDelta,
            west: point.lng - (lngDelta * westMultiplier),
            east: point.lng + (lngDelta * eastMultiplier),
        };
    }

    const latitudes = points.map((point) => point.lat);
    const longitudes = points.map((point) => point.lng);
    const south = Math.min(...latitudes);
    const north = Math.max(...latitudes);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);
    const centerLat = (south + north) / 2;
    const latSpan = north - south;
    const lngSpan = east - west;

    const latOffset = Math.max(latSpan * LOCK_IN_PLACE_PADDING_FACTOR, metersToLatDegrees(MIN_LOCK_RADIUS_METERS));
    const baseLngOffset = Math.max(lngSpan * LOCK_IN_PLACE_PADDING_FACTOR, metersToLngDegrees(MIN_LOCK_RADIUS_METERS, centerLat));

    return {
        south: south - latOffset,
        north: north + latOffset,
        west: west - (baseLngOffset * westMultiplier),
        east: east + (baseLngOffset * eastMultiplier),
    };
}

function toPaddingObject(padding?: Padding): ResolvedPadding {
    if (typeof padding === 'number') {
        return {
            top: padding,
            right: padding,
            bottom: padding,
            left: padding,
        };
    }

    return {
        top: padding?.top ?? 0,
        right: padding?.right ?? 0,
        bottom: padding?.bottom ?? 0,
        left: padding?.left ?? 0,
    };
}

const FitBounds = memo(function FitBounds({ points, padding, overlayPadding, lockInPlace }: {
    points: LatLngLiteral[];
    padding?: Padding;
    overlayPadding?: Padding;
    lockInPlace?: boolean;
}) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        let idleListener: google.maps.MapsEventListener | null = null;

        if (!points?.length) {
            map.setOptions({ restriction: undefined });
            return;
        }

        // Always release previous restriction first, otherwise fitBounds can be constrained
        // and fail to include the new fit points.
        map.setOptions({ restriction: undefined });

        const applyRestriction = () => {
            if (!lockInPlace) {
                map.setOptions({ restriction: undefined });
                return;
            }

            idleListener = google.maps.event.addListenerOnce(map, 'idle', () => {
                const visibleBounds = map.getBounds();
                const { westMultiplier, eastMultiplier } = resolveHorizontalBias(overlayPadding);

                if (!visibleBounds) {
                    map.setOptions({
                        restriction: {
                            latLngBounds: buildLockBoundsFromPoints(points, overlayPadding),
                            strictBounds: false,
                        },
                    });
                    return;
                }

                map.setOptions({
                    restriction: {
                        latLngBounds: expandBoundsAsymmetric(
                            toLiteralBounds(visibleBounds),
                            LOCK_IN_PLACE_PADDING_FACTOR,
                            westMultiplier,
                            eastMultiplier
                        ),
                        strictBounds: false,
                    },
                });
            });
        };

        // If only one point, set center and a reasonable zoom
        if (points.length === 1) {
            const p = points[0];
            const latDelta = metersToLatDegrees(SINGLE_POINT_FIT_RADIUS_METERS);
            const lngDelta = metersToLngDegrees(SINGLE_POINT_FIT_RADIUS_METERS, p.lat);

            const bounds = new google.maps.LatLngBounds(
                { lat: p.lat - latDelta, lng: p.lng - lngDelta },
                { lat: p.lat + latDelta, lng: p.lng + lngDelta }
            );
            map.fitBounds(bounds, padding);
            applyRestriction();
        } else {
            const bounds = new google.maps.LatLngBounds();
            points.forEach(p => bounds.extend(p));

            // Let Google pick the zoom to fit them all
            const expandedFitBounds = expandBounds(
                {
                    south: bounds.getSouthWest().lat(),
                    west: bounds.getSouthWest().lng(),
                    north: bounds.getNorthEast().lat(),
                    east: bounds.getNorthEast().lng(),
                },
                FIT_BOUNDS_PADDING_FACTOR
            );
            map.fitBounds(expandedFitBounds, padding);
            applyRestriction();
        }

        return () => {
            idleListener?.remove();
        };
    }, [map, lockInPlace, JSON.stringify(points), JSON.stringify(padding), JSON.stringify(overlayPadding)]);

    return null;
});

export default FitBounds;
